import React, { useState } from "react";
import "./CSS/ProcessDiagram.css";
import DiagramSvg from "../../../Assets/diagram.svg";
import dropdownIcon from "../../../Assets/arrow-down.svg";

const VIDEO_URLS = {

  modeling: "/assets/strategy-videos/Modeling.mp4",
  mandModeling: "/assets/strategy-videos/MandModel.mp4",
  timeDelay: "/assets/strategy-videos/TimeDelay.mp4",
  beforeReading: "/assets/strategy-videos/BeforeReadingPrompts.mp4",
  duringReading: "/assets/strategy-videos/DuringReadingPrompts.mp4",
  afterReading: "/assets/strategy-videos/AfterReadingPrompts.mp4",
};

const renderVideo = (key, open) => {
  if (!open) return null;

  return (
    <div className="video-wrapper">
      <video
        src={VIDEO_URLS[key]}
        controls
        playsInline
        className="w-full rounded-lg shadow-md mt-2"
      />
    </div>
  );
};

const Arrow = ({ open }) => (
  <img
    src={dropdownIcon}
    alt=""
    className={`dropdown-arrow ${open ? "" : "rotated"}`}
  />
);

export default function ProcessDiagram() {
  const [activeTab, setActiveTab] = useState("diagram");
  const [openCard, setOpenCard] = useState(null);
  const [openPrompt, setOpenPrompt] = useState(null);

  const promptCards = [
    {
      key: "beforeReading",
      title: "Before Reading Prompts",
      body: (
        <div className="prompt-grid">
          <div>
            <h4 className="font-semibold mb-2">Reading Prompt</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Review the fidelity checklist</li>
              <li>Sit in a comfortable and consistent area</li>
              <li>Offer two book choices</li>
              <li>
                <u>Present the Book</u>: Say the <em>title</em> and <em>author</em> of the book to the child before beginning to read.
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Example</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Review the bookmark right before the session</li>
              <li>A consistent, distraction-free environment</li>
              <li>“Which book do you want to read?”</li>
              <li>
                This book is <em>I Was So Mad?</em> and the words are written by <em>Mercer Mayer</em>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      key: "duringReading",
      title: "During Reading Prompts",
      body: (
        <div className="prompt-grid">
          <div>
            <h4 className="font-semibold mb-2">Reading Prompt</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Use each Modeling and Mand-Model strategy at least 3 times</li>
              <li>Use Time Delay strategy at least 3 times</li>
              <li>Provide feedback statements</li>
              <li>Use Attention getters</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Example</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Feedback Statements:</strong>
                <ul className="list-disc list-inside ml-5">
                  <li>“I like how you are sitting so nicely with mommy!”</li>
                  <li>“Good job turning the page.”</li>
                  <li>“You’re right” or “Yes, this is a <em>truck</em>”</li>
                </ul>
              </li>
              <li>
                <strong>Attention Getters:</strong>
                <ul className="list-disc list-inside ml-5">
                  <li><em>“Look!”</em></li>
                  <li><em>“Wow!”</em> (while making a comment on picture or story)</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      key: "afterReading",
      title: "After Reading Prompts",
      body: (
        <div className="prompt-grid">
          <div>
            <h4 className="font-semibold mb-2">Reading Prompt</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Say, “All done” or “The end”</li>
              <li>Thank your child for reading with you</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Example</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>“All done!”</li>
              <li>“Thank you for reading with me today.”</li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  const strategyCards = [
    {
      key: "readingPrompts",
      title: "Using Reading Prompts",
      body: (
        <>
          <p className="mb-3">
            Special reading prompts can boost your child’s engagement
            <em> before</em>, <em>during</em>, and <em>after</em> you read.
            Click a group below to see what to say and watch a quick demo.
          </p>

          {promptCards.map(({ key, title, body }) => (
            <div
              key={key}
              className={`sub-card ${openPrompt === key ? "open" : ""}`}
            >
              <div className="sub-card-header" onClick={(e) => {
                e.stopPropagation();
                setOpenPrompt(prev => (prev === key ? null : key));
              }}>
                {title}
                <Arrow open={openPrompt === key} />
              </div>
              <div className="sub-card-body">
                {renderVideo(key, openPrompt === key)}
                {body}
              </div>
            </div>
          ))}
        </>
      ),
    },

    {
      key: "modeling",
      title: "Modeling Strategy",
      body: (
        <>
          <p>These strategies help your child learn and use new words.</p>
          <p className="mt-2 font-medium">Modeling means showing the word:</p>
          <ul className="list-disc list-inside ml-5">
            <li>You point to a picture and say, “car.”</li>
          </ul>

          <p className="mt-4 font-medium">Steps</p>
          <ol className="list-decimal list-inside ml-5 space-y-1">
            <li>
              <strong>Join your child’s attention</strong> – Look at what your child is focused on in the book.
            </li>
            <li>
              <strong>Model or ask</strong> – Say the word or ask a simple question.
            </li>
            <li>
              <strong>Pause and wait</strong> – Wait 3-5 seconds to give your child time.
            </li>
            <li>
              <strong>Respond</strong> – Smile, praise, and repeat their correct response or gently try again.
            </li>
          </ol>

          <p className="mt-4">
            These steps make learning feel natural and fun.
          </p>
        </>
      ),
    },

    {
      key: "mandModeling",
      title: "Mand-Model Strategy",
      body: (
        <>
          <p>Mand-Model means encouraging your child to respond:</p>
          <ul className="list-disc list-inside ml-5">
            <li>You say, “Where is the car?” or “Say ‘car.’”</li>
          </ul>

          <p className="mt-4 font-medium">Steps</p>
          <ol className="list-decimal list-inside ml-5 space-y-1">
            <li>
              <strong>Join your child’s attention</strong> – Look at what your child is focused on in the book.
            </li>
            <li>
              <strong>Model or ask</strong> – Say the word or ask a simple question.
            </li>
            <li>
              <strong>Pause and wait</strong> – Wait 3-5 seconds to give your child time.
            </li>
            <li>
              <strong>Respond</strong> – Smile, praise, and repeat their correct response or gently try again.
            </li>
          </ol>

          <p className="mt-4">
            These steps make learning feel natural and fun.
          </p>
        </>
      ),
    },

    {
      key: "timeDelay",
      title: "Time Delay Strategy",
      body: (
        <>
          <p>
            This strategy encourages your child to speak or gesture by giving them time to fill in a missing word or phrase.
          </p>

          <p className="mt-2 font-medium">Example:</p>
          <p>You point to a ball and say, “This is a _____,” and wait.</p>

          <p className="mt-4 font-medium">Steps</p>
          <ol className="list-decimal list-inside ml-5 space-y-1">
            <li>
              <strong>Join your child’s attention</strong> – Focus on what they are looking at in the book.
            </li>
            <li>
              <strong>Leave a sentence incomplete</strong> – Start reading a sentence, then stop before the last word.
            </li>
            <li>
              <strong>Wait 3-5 seconds</strong> – Look expectantly at your child.
            </li>
            <li>
              <strong>Respond</strong> – Repeat their word, praise their effort, or say the phrase again if they don’t respond.
            </li>
          </ol>

          <p className="mt-4 italic">
            Extra Tip: Re-read favorite pages and rhymes often. Familiar stories help your child remember and participate!
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="process-diagram-container">
      <div className="process-diagram-header">
        <div className="title-with-dot-process">
          <span className="purple-dot" />
          <h2>About</h2>
        </div>
      </div>
      <div className="purple-divider" />
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === "diagram" ? "active" : ""}`}
          onClick={() => setActiveTab("diagram")}
        >
          About PiCSS
        </button>
        <button
          className={`tab-button ${activeTab === "team" ? "active" : ""}`}
          onClick={() => setActiveTab("team")}
        >
          About Team
        </button>
      </div>

      {activeTab === "team" && (
        <div className="about-description mb-8">
          <p>
            We’re a small but focused team driven by the goal of making
            AI-powered parenting tools both accessible and intuitive.
          </p>
        </div>
      )}

      {activeTab === "diagram" && (
        <div className="about-description mb-8">
          <h3 className="font-semibold mb-2">
            About the PiCSS Program: Helping Your Child Communicate Through
            Stories
          </h3>
          <p className="mb-4">
            During storybook reading you can use simple, fun strategies to help
            your child communicate. These strategies encourage noticing words,
            turn-taking, and confidence while reading together.
          </p>
          <p className="text-sm text-blue-700">
            For detailed information on these strategies,&nbsp;
            <a
              href="https://buffalo.box.com/s/zbfp0nbswkz8fjswatwsow40vsc0ugne"
              target="_blank"
              rel="noopener noreferrer"
              className="click-here-link"
            >
              click here
            </a>.
          </p>

          {strategyCards.map(({ key, title, body }) => {
            const isOpen = openCard === key;
            const hasVideo = VIDEO_URLS[key];

            return (
              <div
                key={key}
                className={`strategy-card ${isOpen ? "open" : ""}`}
              >
                <div className="card-header" onClick={() => setOpenCard(prev => (prev === key ? null : key))}>
                  <span className="font-medium">{title}</span>
                  <Arrow open={isOpen} />
                </div>

                <div className="card-body">
                  {hasVideo && renderVideo(key, isOpen)}
                  {body}
                </div>
              </div>
            );
          })}
          <div className="diagram-info-container mt-6">
            <h4 className="diagram-title">PiCSS Flowchart</h4>
            <p>
              This diagram illustrates a four-step strategy for parents to engage meaningfully with their children during storybook reading. </p>
            <p>It visualizes key phases like joint attention, tailored language strategies, and responsive feedback—helping parents foster communication, especially for children with autism.
            </p>

            <div className="process-illustration-container">
              <div className="image-wrapper relative">
                <img
                  src={DiagramSvg}
                  alt="Process Diagram"
                  className="process-diagram"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}